import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Link2, UserX, Crown, Phone, CalendarClock, AlertCircle } from "lucide-react";
import { differenceInDays } from "date-fns";
import { useTranslation } from "react-i18next";
import type { CustomerCategory } from "./types";
import {
  type CustomerListRow,
  formatPhone,
  formatLastBooking,
  formatUnpaid,
  hasUnpaid,
  paginationState,
} from "@/lib/customerListDisplay";

export function CustomerTable({
  customers,
  categories,
  isLoading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onSelectCustomer,
}: {
  customers: CustomerListRow[];
  categories: CustomerCategory[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onSelectCustomer: (customer: CustomerListRow) => void;
}) {
  const { t, i18n } = useTranslation("admin");
  if (isLoading) {
    return <p className="text-sm text-muted-foreground text-center py-8">Laddar...</p>;
  }

  if (customers.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">Inga kunder hittades</p>;
  }

  return (
    <>
      <div className="grid gap-2">
        {customers.map((c) => {
          const cat = c.category_id ? categories.find((cc) => cc.id === c.category_id) : null;
          const mConfig = cat?.membership_config as Record<string, unknown> | null;
          return (
            <Card
              key={c.id}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => onSelectCustomer(c)}
            >
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm">{c.full_name || c.email}</p>
                      {c.customer_number && (
                        <Badge variant="outline" className="text-[10px] font-mono">K-{c.customer_number}</Badge>
                      )}
                      {/* V2 Issue 4.4 (strama): Ny = recent-signup utan bokning
                        *  ELLER recent första-bokning. Tidigare logik markerade
                        *  alla customers med NULL first_booking_at som Ny vilket
                        *  gav ~5614 felaktiga Ny-badges på AP Nacka (importerade
                        *  men aldrig bokat). Nu:
                        *  - !first_booking_at + recent(created_at) → Ny (lead)
                        *  - first_booking_at + recent(first_booking_at) → Ny (nybliven)
                        *  - gammal import / etablerad kund → INTE Ny
                        *  Ref: audit-reports/emergency-diagnos-ny-badge/rapport.md */}
                      {(
                        (!c.first_booking_at &&
                          differenceInDays(new Date(), new Date(c.created_at)) <= 14)
                        ||
                        (c.first_booking_at &&
                          differenceInDays(new Date(), new Date(c.first_booking_at)) <= 14)
                      ) && (
                        <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20">Ny</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                    {/* 010: telefon · senaste bokning · obetalt. Listan är en
                        kortlista — samma layout på desktop och smal vy — så
                        kolumnerna blir en metarad, inte tabellceller. */}
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1" title={t("customerFields.phone")}>
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="tabular-nums">{formatPhone(c.phone)}</span>
                      </span>
                      <span className="inline-flex items-center gap-1" title={t("customersPage.lastBooking")}>
                        <CalendarClock className="h-3 w-3 shrink-0" />
                        <span>{formatLastBooking(c.last_booking_at, i18n.language)}</span>
                      </span>
                      {hasUnpaid(c) && (
                        <span
                          className="inline-flex items-center gap-1 font-medium text-destructive"
                          title={t("customersPage.unpaidTitle", { count: c.unpaid_count })}
                        >
                          <AlertCircle className="h-3 w-3 shrink-0" />
                          <span className="tabular-nums">{formatUnpaid(c.unpaid_minor)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {mConfig && (
                    <Badge variant="outline" className="text-xs">
                      <Crown className="h-3 w-3 mr-1" />
                      {mConfig.name || cat?.name}
                    </Badge>
                  )}
                  {c.user_id ? (
                    <Badge className="text-xs bg-green-600/15 text-green-700 border-green-300">
                      <Link2 className="h-3 w-3 mr-1" /> Kopplad
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      <UserX className="h-3 w-3 mr-1" /> Ej registrerad
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination — 010: sant totalantal ur count(*) over () i samma anrop.
          "Nästa" gissades tidigare via `customers.length < pageSize`, vilket är
          fel på exakt full sista sida. */}
      {(() => {
        const p = paginationState({ page, pageSize, rows: customers.length, totalCount });
        return (
          <div className="flex items-center justify-between">
            <button className="text-xs text-primary disabled:opacity-50" disabled={!p.hasPrev} onClick={() => onPageChange(page - 1)}>← Föregående</button>
            <span className="text-xs text-muted-foreground tabular-nums">
              {t("customersPage.range", { from: p.from, to: p.to, total: p.total })}
            </span>
            <button className="text-xs text-primary disabled:opacity-50" disabled={!p.hasNext} onClick={() => onPageChange(page + 1)}>Nästa →</button>
          </div>
        );
      })()}
    </>
  );
}
